import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";
import { useUserFeedback } from "@/hooks/useUserFeedback";

interface ModalSignalerProblemeProps {
  open: boolean;
  onClose: () => void;
}

export default function ModalSignalerProbleme({ open, onClose }: ModalSignalerProblemeProps) {
  const [type, setType] = useState("Technique");
  const [message, setMessage] = useState("");
  const { sendFeedback, loading, error, success } = useUserFeedback();
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    await sendFeedback({
      feedbackType: "probleme",
      problemeType: type,
      message,
    });
  }


    // Gérer la transition success → sent local avec message + auto reset sur 3s ou plus
  useEffect(() => {
    if (success) {
      setSent(true);
      const timeout = setTimeout(() => {
        setSent(false);
        setMessage("");
        setType("Technique");
      }, 5000); // message visible 3 secondes

      return () => clearTimeout(timeout);
    }
  }, [success]);

  // Reset si modal fermé
  useEffect(() => {
    if (!open) {
      setSent(false);
      setMessage("");
      setType("Technique");
    }
  }, [open,onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="modal-bg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.15)",
            zIndex: 10001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={onClose}
        >
          <motion.div
            key="modal-card"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            style={{
              background: "var(--background, #fff)",
              borderRadius: 12,
              padding: 28,
              minWidth: 340,
              minHeight: 180,
              boxShadow: "0 4px 24px rgba(0,0,0,0.17)",
              position: "relative",
              maxWidth: "94vw",
              width: 400,
              zIndex: 10002,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              style={{
                border: "none",
                background: "none",
                position: "absolute",
                top: 14,
                right: 16,
                cursor: "pointer",
                padding: 3,
                borderRadius: 5,
                color: "var(--muted, #666)",
              }}
              aria-label="Fermer"
            >
              <X size={22} />
            </button>
            <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "0 0 14px" }}>
              <AlertTriangle size={28} color="#faa61a" />
              <h2 style={{ fontWeight: 600, fontSize: "1.18rem", margin: 0 }}>Signaler un problème</h2>
            </div>

            {!sent ? (
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 12 }}>
                <div>
                  <label htmlFor="typepb" style={{ fontWeight: 500, fontSize: 13 }}>
                    Type de problème
                  </label>
                  <br />
                  <select
                    id="typepb"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "7px",
                      borderRadius: 4,
                      border: "1px solid #ddd",
                      marginTop: 3,
                    }}
                    disabled={loading}
                  >
                    <option>Technique</option>
                    <option>Contenu erroné</option>
                    <option>Suggestion</option>
                    <option>Autre</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="msgpb" style={{ fontWeight: 500, fontSize: 13 }}>
                    Votre message
                  </label>
                  <textarea
                    id="msgpb"
                    required
                    maxLength={1000}
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Décrivez précisément le problème…"
                    style={{
                      width: "100%",
                      resize: "vertical",
                      padding: "7px",
                      borderRadius: 4,
                      border: "1px solid #ddd",
                      marginTop: 3,
                    }}
                    disabled={loading}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || message.trim().length === 0}
                  style={{
                    background: "#faa61a",
                    color: "#232323",
                    fontWeight: 600,
                    border: "none",
                    borderRadius: 7,
                    padding: "10px 0",
                    fontSize: "1rem",
                    marginTop: 2,
                    cursor: loading ? "wait" : "pointer",
                    opacity: message.trim().length === 0 ? 0.65 : 1,
                    transition: "background 0.19s",
                  }}
                >
                  {loading ? "Envoi..." : "Envoyer"}
                </button>
                {error && (
                  <div style={{ color: "red", fontWeight: "600", marginTop: 10 }}>
                    {error}
                  </div>
                )}
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{ color: "green", marginTop: 20, textAlign: "center" }}
              >
                Merci pour votre signalement.
                <br />
                L’équipe en prendra connaissance rapidement.
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
